/**
 * LessonPages
 * lessonPages
 */

import { PropsWithChildren, createContext, useContext, useReducer } from "react";
import { produce } from "immer";
import ILessonPageDto from "../../models/ILessonPageDto";
import { AGGREGATION } from "../../components/constants/pageTypes";

/**
 * the time sheet is a data structure to help with the viewing of a sheet with objects
 * in relation to time for example maybe something like this:
 * TimeSheet {
 *   objectList: Object[]
 * }
 *
 */

export enum LessonPagesActions {
  UPDATE_CURRENT_PAGE,
  UPDATE_LESSON_PAGES, // currently dumbly setting the lesson pages
  UPDATE_PAGE_ORDER,
  SET_CURRENT_PAGE_INDEX,
  UPDATE_CURRENT_PAGE_MANIFEST,
  UPDATE_PAGE_MANIFEST_BY_ID,
  ADD_ANIMATED_OBJECT,
  ADD_NEW_PAGE,
  UPDATE_SEQUENCE_LENGTH,
  UPDATE_SELECTED_OBJECT_FRAMES,
  SET_OBJECTS_ON_PAGE_MANIFEST,
  DELETE_PAGE,
  UPDATE_SELECTED_PANEL,
  UPDATE_SELECTED_POINT_ID,
  SET_CURRENT_AGGREGATION_ORDER,
  UPDATE_PAGE_BY_ORDER,
}

interface LessonPagesState {
  lessonPages: any[];
  currentLessonPage: ILessonPageDto | null;
  currentLessonPageIndex: number;
  // NOTE: Despite the name, this is used as an INDEX into lessonPages[], not as an order value.
  // The naming is legacy - it works because referenceIdLogic() ensures order === index.
  currentAggregationOrder: number | null;
  objects: any[];
  selectedObject: any;
  selectedPanel: "properties" | "page" | "advanced";
  selectedPointId: null | string;
}

type LessonPagesAction = {
  type:
    | LessonPagesActions.UPDATE_CURRENT_PAGE
    | LessonPagesActions.UPDATE_PAGE_MANIFEST_BY_ID
    | LessonPagesActions.UPDATE_LESSON_PAGES
    | LessonPagesActions.SET_CURRENT_PAGE_INDEX
    | LessonPagesActions.UPDATE_CURRENT_PAGE_MANIFEST
    | LessonPagesActions.ADD_ANIMATED_OBJECT
    | LessonPagesActions.SET_OBJECTS_ON_PAGE_MANIFEST
    | LessonPagesActions.ADD_NEW_PAGE
    | LessonPagesActions.DELETE_PAGE
    | LessonPagesActions.UPDATE_PAGE_ORDER
    | LessonPagesActions.UPDATE_SEQUENCE_LENGTH
    | LessonPagesActions.UPDATE_SELECTED_PANEL
    | LessonPagesActions.UPDATE_SELECTED_POINT_ID
    | LessonPagesActions.SET_CURRENT_AGGREGATION_ORDER
    | LessonPagesActions.UPDATE_PAGE_BY_ORDER;
  payload?: any;
};
type LessonPagesReducer = (s: LessonPagesState, a: LessonPagesAction) => LessonPagesState;

type LessonPagesDispatch = (action: LessonPagesAction) => void;
const initialState: LessonPagesState = {
  lessonPages: [],
  currentLessonPage: null,
  currentLessonPageIndex: 0,
  currentAggregationOrder: null,
  objects: [],
  selectedObject: null,
  selectedPanel: "properties",
  selectedPointId: null,
};

const LessonPagesState = createContext<LessonPagesState>(initialState);
const LessonPagesDispatch = createContext<any>({});

const getCurrentLessonSelection = (
  lessonPages: ILessonPageDto[],
  currentAggregationOrder: number | null,
  currentLessonPageIndex: number,
) => {
  if (currentAggregationOrder !== null) {
    const aggregation = lessonPages[currentAggregationOrder];
    const nestedPages = aggregation?.pageManifest?.Pages;

    if (Array.isArray(nestedPages) && nestedPages.length > 0) {
      const safeNestedIndex = Math.min(currentLessonPageIndex, nestedPages.length - 1);

      return {
        currentLessonPage: nestedPages[safeNestedIndex],
        currentLessonPageIndex: safeNestedIndex,
        currentAggregationOrder,
      };
    }
  }

  if (lessonPages.length === 0) {
    return {
      currentLessonPage: null,
      currentLessonPageIndex: 0,
      currentAggregationOrder: null,
    };
  }

  const fallbackIndex = Math.min(currentAggregationOrder ?? currentLessonPageIndex, lessonPages.length - 1);

  return {
    currentLessonPage: lessonPages[fallbackIndex],
    currentLessonPageIndex: fallbackIndex,
    currentAggregationOrder: null,
  };
};

const lessonPagesReducer = (state: LessonPagesState, action: LessonPagesAction): LessonPagesState => {
  switch (action.type) {
    case LessonPagesActions.UPDATE_CURRENT_PAGE: {
      const updates = action.payload;

      const lessonPages = state.lessonPages.map((page, index) => {
        if (index === state.currentLessonPageIndex) {
          return {
            ...page,
            ...updates,
          };
        }
        return page;
      });

      return {
        ...state,
        lessonPages,
        currentLessonPage: action.payload,
      };
    }
    case LessonPagesActions.UPDATE_CURRENT_PAGE_MANIFEST: {
      const updates = action.payload;
      const { currentAggregationOrder, currentLessonPageIndex } = state;

      const lessonPages = produce(state.lessonPages, (draft) => {
        if (currentAggregationOrder !== null) {
          const aggregationPage = draft[currentAggregationOrder];
          const nestedPage = aggregationPage?.pageManifest?.Pages?.[currentLessonPageIndex];

          if (!nestedPage) {
            return;
          }

          aggregationPage.pageManifestIsDirty = true;
          nestedPage.pageManifestIsDirty = true;
          nestedPage.pageManifest = {
            ...nestedPage.pageManifest,
            ...updates,
          };
          return;
        }

        const currentPage = draft[currentLessonPageIndex];
        if (!currentPage) {
          return;
        }

        currentPage.pageManifestIsDirty = true;
        currentPage.pageManifest = {
          ...currentPage.pageManifest,
          ...updates,
        };
      });

      const currentSelection = getCurrentLessonSelection(lessonPages, currentAggregationOrder, currentLessonPageIndex);

      return {
        ...state,
        lessonPages,
        ...currentSelection,
      };
    }
    case LessonPagesActions.UPDATE_PAGE_ORDER: {
      const { pageToGoTo, pageIndex, isAggregation } = action.payload;
      const updatePageOrder = produce((lessonPages) => {
        const pageToMove = lessonPages[pageIndex];
        lessonPages.splice(pageIndex, 1);
        lessonPages.splice(pageToGoTo, 0, pageToMove);
        lessonPages.forEach((page: any, index: number) => {
          if (page.order !== index) {
            page.order = index;
            page.lessonPageIsDirty = true;
          }
        });
      });

      const lessonPages = updatePageOrder(state.lessonPages);

      return {
        ...state,
        lessonPages,
        currentLessonPageIndex: isAggregation ? 0 : pageToGoTo,
        currentLessonPage: isAggregation ? lessonPages[pageToGoTo].pageManifest.Pages[0] : lessonPages[pageToGoTo],
        currentAggregationOrder: isAggregation ? pageToGoTo : null,
      };
    }
    case LessonPagesActions.UPDATE_PAGE_MANIFEST_BY_ID: {
      const { pageId, pageManifest } = action.payload;
      const lessonPages = state.lessonPages.map((page) => {
        if (page.pageVersionId === pageId) {
          return { ...page, pageManifest };
        }
        return page;
      });

      return {
        ...state,
        lessonPages,
      };
    }
    case LessonPagesActions.UPDATE_LESSON_PAGES: {
      const { currentAggregationOrder, currentLessonPageIndex } = state;
      const incoming = action.payload as any[];

      // A stale UPDATE_LESSON_PAGES payload (e.g. from builder.tsx's
      // local `lessonPages` useState, which lags behind this provider after a
      // SET_OBJECTS_ON_PAGE_MANIFEST dispatch) must not silently wipe page
      // manifests that already hold unsaved edits. For any page that is dirty
      // here but arrives clean in the payload, keep the current manifest.
      const currentByVersionId = new Map(state.lessonPages.map((page) => [page.pageVersionId, page]));
      const lessonPages = incoming.map((incomingPage) => {
        const existing =
          incomingPage?.pageVersionId != null ? currentByVersionId.get(incomingPage.pageVersionId) : undefined;
        if (existing?.pageManifestIsDirty && !incomingPage?.pageManifestIsDirty) {
          return {
            ...incomingPage,
            pageManifest: existing.pageManifest,
            pageManifestIsDirty: true,
          };
        }
        return incomingPage;
      });

      const currentSelection = getCurrentLessonSelection(lessonPages, currentAggregationOrder, currentLessonPageIndex);

      return {
        ...state,
        lessonPages,
        ...currentSelection,
      };
    }
    case LessonPagesActions.UPDATE_SEQUENCE_LENGTH: {
      const sequenceLength = action.payload;
      const { currentLessonPageIndex, currentAggregationOrder } = state;
      const updateSequenceLength = produce((lessonPages) => {
        if (currentAggregationOrder !== null) {
          // Inside an aggregation - update the nested page
          const aggregation = lessonPages[currentAggregationOrder];
          aggregation.pageManifestIsDirty = true;
          const pm = aggregation.pageManifest.Pages[currentLessonPageIndex].pageManifest;
          if (!pm.timeline) {
            pm.timeline = {
              animatedObjects: [],
              sequenceLength,
            };
          }
          pm.timeline.sequenceLength = sequenceLength;
        } else {
          // Regular page
          lessonPages[currentLessonPageIndex].pageManifestIsDirty = true;
          const pm = lessonPages[currentLessonPageIndex].pageManifest;
          if (!pm.timeline) {
            pm.timeline = {
              animatedObjects: [],
              sequenceLength,
            };
          }
          pm.timeline.sequenceLength = sequenceLength;
        }
      });
      const lessonPages = updateSequenceLength(state.lessonPages);

      // Get the correct currentLessonPage
      const currentLessonPage =
        currentAggregationOrder !== null
          ? lessonPages[currentAggregationOrder].pageManifest.Pages[currentLessonPageIndex]
          : lessonPages[currentLessonPageIndex];

      return {
        ...state,
        lessonPages,
        currentLessonPage,
      };
    }
    case LessonPagesActions.SET_CURRENT_PAGE_INDEX: {
      const newIndex = action.payload;
      const { currentAggregationOrder } = state;
      // Do not clamp via helper here — newIndex is the user's explicit choice and
      // may legitimately exceed the nested ML page count when transitioning back
      // to a base page (useGoToPage dispatches SET_CURRENT_AGGREGATION_ORDER next).
      const currentLessonPage =
        currentAggregationOrder !== null
          ? state.lessonPages[currentAggregationOrder]?.pageManifest?.Pages?.[newIndex] ?? null
          : state.lessonPages[newIndex] ?? null;

      return {
        ...state,
        currentLessonPage,
        currentLessonPageIndex: newIndex,
      };
    }
    case LessonPagesActions.SET_CURRENT_AGGREGATION_ORDER: {
      const newAggregationOrder = action.payload;
      const { currentLessonPageIndex } = state;
      // Recompute currentLessonPage against the *new* aggregation order. Without
      // this, switching from a microlesson page back to a base page leaves
      // currentLessonPage pointing at lessonPages[prevAgg].Pages[index] — the
      // page at the same index *inside* the previously selected microlesson.
      const currentLessonPage =
        newAggregationOrder !== null && newAggregationOrder !== undefined
          ? state.lessonPages[newAggregationOrder]?.pageManifest?.Pages?.[currentLessonPageIndex] ?? null
          : state.lessonPages[currentLessonPageIndex] ?? null;

      return {
        ...state,
        currentAggregationOrder: newAggregationOrder,
        currentLessonPage,
      };
    }
    case LessonPagesActions.ADD_NEW_PAGE: {
      const newPage = action.payload.page;
      const newPageIndex = action.payload.pageIndex;
      const addNewPage = produce((lessonPages) => {
        lessonPages.splice(newPageIndex, 0, newPage);
        lessonPages.forEach((page: any, index: number) => {
          if (page.order !== index) {
            page.order = index;
            page.lessonPageIsDirty = true;
          }
        });
      });

      const lessonPages = addNewPage(state.lessonPages);
      const isAggregation = newPage.pagePlayerType === AGGREGATION;
      const currentAggregationOrder = isAggregation ? newPageIndex : null;
      // For aggregation pages, show the first nested page (index 0)
      // For regular pages, show the page at newPageIndex
      const currentLessonPage = isAggregation
        ? lessonPages[newPageIndex].pageManifest.Pages[0]
        : lessonPages[newPageIndex];
      const currentLessonPageIndex = isAggregation ? 0 : newPageIndex;

      return {
        ...state,
        lessonPages,
        currentLessonPageIndex,
        currentLessonPage,
        currentAggregationOrder,
      };
    }
    case LessonPagesActions.DELETE_PAGE: {
      const {
        currentAggregationOrder,
        currentlyDisplayedPageIndex,
        indexToDelete,
        lessonPages: sourceLessonPages,
      } = action.payload;

      const deletePage = produce((lessonPages) => {
        lessonPages.splice(indexToDelete, 1);
        lessonPages.forEach((page: any, index: number) => {
          if (page.order !== index) {
            page.order = index;
            page.lessonPageIsDirty = true;
          }
        });
      });
      const lessonPages = deletePage(sourceLessonPages ?? state.lessonPages);

      // Get the correct currentLessonPage based on whether we're in an aggregation
      const currentLessonPage =
        currentAggregationOrder !== null
          ? lessonPages[currentAggregationOrder]?.pageManifest?.Pages?.[currentlyDisplayedPageIndex]
          : lessonPages[currentlyDisplayedPageIndex];

      return {
        ...state,
        lessonPages,
        currentAggregationOrder,
        currentLessonPage,
        currentLessonPageIndex: currentlyDisplayedPageIndex,
      };
    }
    case LessonPagesActions.ADD_ANIMATED_OBJECT: {
      const objectId = action.payload;
      const type = action.type;
      const { currentLessonPageIndex, currentAggregationOrder } = state;
      const addAnimatedObject = produce((lessonPages) => {
        let pm;
        if (currentAggregationOrder !== null) {
          // Inside an aggregation - update the nested page
          const aggregation = lessonPages[currentAggregationOrder];
          aggregation.pageManifestIsDirty = true;
          pm = aggregation.pageManifest.Pages[currentLessonPageIndex].pageManifest;
        } else {
          // Regular page
          lessonPages[currentLessonPageIndex].pageManifestIsDirty = true;
          pm = lessonPages[currentLessonPageIndex].pageManifest;
        }
        // if there is not a timeline object in the page manifest add the default one
        if (!pm.timeline) {
          pm.timeline = {
            animatedObjects: [],
            sequenceLength: 30,
          };
        }
        pm.timeline.animatedObjects.push({
          id: objectId,
          start: 0,
          end: null,
          type,
        });
      });
      const lessonPages = addAnimatedObject(state.lessonPages);

      // Get the correct currentLessonPage
      const currentLessonPage =
        currentAggregationOrder !== null
          ? lessonPages[currentAggregationOrder].pageManifest.Pages[currentLessonPageIndex]
          : lessonPages[currentLessonPageIndex];

      return {
        ...state,
        lessonPages,
        currentLessonPage,
      };
    }
    case LessonPagesActions.SET_OBJECTS_ON_PAGE_MANIFEST: {
      const {
        animatedObjects,
        images,
        textBoxes,
        annotations,
        videos,
        scorm,
        tables,
        hotspots,
        smartObjects,
        panoramicList,
        shapeList,
        replacedAssets,
        narrationAudios,
        audioEffects,
      } = action.payload; // in the flattened format, need to convert to pageManifest

      const updatePageManifest = produce((lessonPages) => {
        const { currentLessonPageIndex, currentAggregationOrder } = state;
        const isInsideAggregation = currentAggregationOrder !== null;
        const page = lessonPages[isInsideAggregation ? currentAggregationOrder : currentLessonPageIndex];
        const pm = isInsideAggregation
          ? page.pageManifest.Pages[currentLessonPageIndex].pageManifest
          : page.pageManifest;
        const currentPage = isInsideAggregation ? page.pageManifest.Pages[currentLessonPageIndex] : page;

        // Determine if this is a freeForm Base page - only freeForm pages should have
        // timeline, smartObjects, audioEffects and other advanced properties
        const pagePlayerType = currentPage.pagePlayerType;
        const basePageType = pm?.basePageType;
        const isFreeFormBasePage = pagePlayerType === "Base" && basePageType === "freeForm";

        if (replacedAssets) {
          pm.replacedAssets = replacedAssets;
          currentPage.pageManifestIsDirty = true;
        }
        // hotspots are only for freeForm pages
        if (hotspots && isFreeFormBasePage) {
          pm.hotspots = hotspots;
          currentPage.pageManifestIsDirty = true;
        }
        if (images) {
          pm.pageImage = images;
          currentPage.pageManifestIsDirty = true;
        }
        if (textBoxes) {
          pm.textBlock = textBoxes;
          currentPage.pageManifestIsDirty = true;
        }
        if (annotations) {
          pm.annotations = annotations;
          currentPage.pageManifestIsDirty = true;
        }
        // panoramicList is only for freeForm pages
        if (panoramicList && isFreeFormBasePage) {
          pm.panoramicList = produce(panoramicList, (draft) => {
            // delete the moduleRef property, it is not needed in the page manifest
            draft.forEach((panorama) => {
              delete panorama.moduleRef;
            });
          });
          currentPage.pageManifestIsDirty = true;
        }
        // shapeList is only for freeForm pages
        if (shapeList && isFreeFormBasePage) {
          pm.shapeList = shapeList;
          currentPage.pageManifestIsDirty = true;
        }
        // timeline/animatedObjects are only for freeForm pages
        if (animatedObjects && isFreeFormBasePage) {
          //lazy initialize timeline
          if (!pm.timeline) {
            pm.timeline = {
              animatedObjects: [],
              sequenceLength: 30,
            };
          }
          pm.timeline.animatedObjects = animatedObjects;
          currentPage.pageManifestIsDirty = true;
        }
        // smartObjects are only for freeForm pages
        if (smartObjects && isFreeFormBasePage) {
          pm.smartObjects = smartObjects;
          currentPage.pageManifestIsDirty = true;
        }
        // tables are only for freeForm pages
        if (tables && isFreeFormBasePage) {
          pm.tables = tables;
          currentPage.pageManifestIsDirty = true;
        }
        // videos are only for freeForm pages
        if (isFreeFormBasePage) {
          if (videos) {
            if (videos[0]) {
              pm.video = videos[0];
              currentPage.pageManifestIsDirty = true;
            } else {
              if (pm.video) {
                delete pm.video;
                currentPage.pageManifestIsDirty = true;
              }
            }
          } else {
            delete pm.video;
          }
        }
        // scorm is only for freeForm pages
        if (isFreeFormBasePage) {
          if (scorm) {
            if (!pm.pageScormObject) {
              pm.pageScormObject = [];
            }
            pm.pageScormObject[0] = scorm;
            currentPage.pageManifestIsDirty = true;
          } else {
            if (pm.pageScormObject) {
              delete pm.pageScormObject;
              currentPage.pageManifestIsDirty = true;
            }
          }
        }
        if (narrationAudios) {
          pm.narrationAudios = narrationAudios;
          currentPage.pageManifestIsDirty = true;
        }
        // audioEffects are only for freeForm pages
        if (audioEffects && isFreeFormBasePage) {
          pm.audioEffects = audioEffects;
          currentPage.pageManifestIsDirty = true;
        }
      });
      const lessonPages = updatePageManifest(state.lessonPages);
      return {
        ...state,
        lessonPages,
      };
    }
    case LessonPagesActions.UPDATE_SELECTED_PANEL: {
      return {
        ...state,
        selectedPanel: action.payload,
      };
    }
    case LessonPagesActions.UPDATE_SELECTED_POINT_ID: {
      return {
        ...state,
        selectedPointId: action.payload,
      };
    }
    case LessonPagesActions.UPDATE_PAGE_BY_ORDER: {
      const { order, updates } = action.payload;
      const lessonPages = state.lessonPages.map((page) => {
        if (page.order === order) {
          return {
            ...page,
            ...updates,
            lessonPageIsDirty: true,
          };
        }
        return page;
      });

      return {
        ...state,
        lessonPages,
      };
    }
    default:
      return state;
  }
};

export function LessonPagesProvider({ children }: PropsWithChildren<any>) {
  const [state, dispatch] = useReducer<LessonPagesReducer>(lessonPagesReducer, initialState); // TODO use reducer

  return (
    <LessonPagesDispatch.Provider value={dispatch}>
      <LessonPagesState.Provider value={state}>{children}</LessonPagesState.Provider>
    </LessonPagesDispatch.Provider>
  );
}

export function useLessonPagesDispatch() {
  const ctx = useContext(LessonPagesDispatch);
  if (ctx === undefined) {
    throw new Error("Wrap component in LessonPages Provider");
  }
  return ctx as LessonPagesDispatch;
}
export function useLessonPagesState() {
  const ctx = useContext(LessonPagesState);
  if (ctx === undefined) {
    throw new Error("Wrap component in LessonPages Provider");
  }
  return ctx;
}

// Hook like access
// Ideally we could provide easier functions to update the manifest
export function useLessonPage() {
  const state = useLessonPagesState();
  const dispatch = useLessonPagesDispatch();

  const updateManifest = (payload: unknown) => {
    dispatch({
      type: LessonPagesActions.UPDATE_CURRENT_PAGE_MANIFEST,
      payload,
    });
  };

  return {
    ...state,
    dispatch,
    // Helpers (We can add helpers for each of the available action types)
    pageManifest: state?.currentLessonPage?.pageManifest ?? null,
    updateManifest,
  };
}
